// **عمود التنقّل** (قرار المالك 22/09/2026: «خلّي القوايم رئيسية بدل ما هي أفقية»).
//
// كان التنقّل شريطًا أفقيًّا بواحدٍ وعشرين زرًّا في سطرين. وعيبُه ليس شكلًا: صفٌّ من
// الأزرار **لا بنية له** — لا يقول أين أنت من النظام، ولا ما الذي يجاور ما، ولا أين
// تبحث عمّا لم تفتحه من قبل. ويزداد سوءًا مع كل شاشةٍ تُضاف.
//
// والعمود يحلّ الثلاثة: **أبوابٌ مسمّاة** فتُعرف بنية البرنامج في ثوانٍ، **وعمودٌ يطول**
// فلا تزاحم شاشةٌ جديدة ما قبلها، **وموضعٌ ثابت** فلا تتحرّك الأزرار تحت اليد.
// **وعلى اليمين** لأن الواجهة عربية: العين تبدأ من اليمين، فالتنقّل حيث تبدأ.
//
// **والهيئة** (قرار المالك 22/09/2026 بعد أوّل نسخة: «شكل القائمة سيّئ ومش حاسس عليه
// قيمة، والخط صغير — عاوز زرار بـeffect وألوان مناسبة لتصميم الويب واللوجو، high end
// وشكله rich، والخط واضح ومريح للعين»):
//   · **الخطّ بمقاس نصّ البرنامج** (`text-sm` = 18px على مقياسه المكبَّر درجتين) لا
//     بمقاسٍ مخترعٍ أصغر: القائمة تُقرأ بطرف العين وأنت تشتغل، لا بتحديق. وقد جُرّب
//     15px فكان صغيرًا («الخط ما زال صغير ومش واضح» — قرار المالك)، والبنود بارتفاع
//     48px لا تتلاصق.
//   · **المفتوح قرصٌ بتدرّج أزرق الهوية** بنصٍّ أبيض وظلٍّ ملوّن خفيف، ومعه خطُّ
//     تركوازٍ من اللوجو على حافة البداية — يُرى من طرف العين.
//   · **ولكلِّ بابٍ رمزُه** فوق بنوده — من طقم الرموز نفسه، فيُعرف الباب بالعين.
//   · **ورمزُ كلِّ بندٍ في مربّعٍ خاصّ به**، يتبدّل مع الحالة: رماديٌّ هادئ، فأبيضُ
//     مرتفعٌ عند المرور، فشفّافٌ أبيض حين يُفتح. وهو ما يعطي «الإحساس بالقيمة»:
//     طبقتان لا طبقة، وانتقالٌ يُلمَس لا يُشاهَد.
// ولا لمعانَ ولا تدرّجاتٍ صاخبة: الغِنى في الطبقات والمسافات، لا في الألوان.
//
// **ولا يضيف شاشةً ولا يحذف واحدة**: يستقبل `tabs` كما بنتها `App` بحسب دور صاحب
// الجلسة، ويقسمها أبوابًا (navSections). فالصلاحيات حيث هي، والمسارات كما هي.
//
// **وللهاتف شكلُه** (MobileNav): شريطٌ سفليّ ولوحٌ يُسحب. فهذا داخل `lg:flex` وحدها،
// وذاك لا يُبنى إلّا دون 1024px — شكلان لا يلتقيان.
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { navIcon } from '../components/navIcons.tsx';
import { groupTabs, type NavItem } from './navSections.ts';
import type { Me } from '../lib/api.ts';

/** التثبيت والأبوابُ المطويّة يُحفظان: هيئةُ الشاشة اختيارُ صاحبها، لا يُعاد كل صباح. */
const PINNED_KEY = 'icontravel.sidebar.pinned';
const CLOSED_KEY = 'icontravel.sidebar.closed';

/**
 * **مهلةُ الانكماش** (قرار المالك 22/09/2026: «عاوز القائمة كلها يحصلها طيّ لما أبعّد
 * الماوس عنها، والاختفاء ده يقعد ثانيتين وبعد كده تختفي، وأول ما أروح بالماوس عليه
 * تظهر تاني»). وليست صفرًا عن قصد: من يخرج بالماوس ليرجع — أو يمرّ عابرًا في طريقه
 * إلى المحتوى — لا تنكمش القائمة تحت يده.
 */
const SHRINK_AFTER_MS = 2000;

/** تركواز اللوجو — لمسةُ الهوية بجانب أزرقها. يُستعمل خطًّا وحدًّا لا أرضيةً واسعة. */
const SEA = '#0b7f88';

/*
 * **أرضيةُ العمود من ألوان الشعار** (قرار المالك 22/09/2026: «عاوز القائمة نفسها
 * تبقى واخدة لون من ألوان اللوجو»): سماءُ الشعار (`#8EE1E6`) — لونُ أيقونة التطبيق نفسه —
 * **مخفَّفةً لا صريحة**، فالعمود أرضيةٌ يُقرأ فوقها عشرون سطرًا كل يوم. وهي صنفا
 * `sidebar-surface` و`sidebar-avatar` في index.css لا style مضمَّن: المضمَّن لا يعلو عليه
 * الوضع الداكن (مراجعة 23/09/2026 — كانت الأرضية تبقى فاتحةً تحت نصٍّ صار فاتحًا).
 */

const read = (k: string, fallback = ''): string => {
  try { return localStorage.getItem(k) ?? fallback; } catch { return fallback; }
};
const write = (k: string, v: string): void => {
  try { localStorage.setItem(k, v); } catch { /* تخزينٌ ممنوع: الاختيار لهذه الجلسة */ }
};

const ROLE_AR: Record<Me['role'], string> = { ADMIN: 'إدارة', SALES: 'مبيعات', PARTNER: 'شريك' };

export function Sidebar({ tabs, active, onPick, me }: {
  tabs: readonly NavItem[];
  active: string;
  onPick: (id: string) => void;
  me: Me;
}) {
  /** مثبَّتة = مفتوحةٌ دائمًا ولا تنكمش. وغيرُ المثبَّتة شريطُ رموزٍ يتّسع تحت الماوس. */
  const [pinned, setPinned] = useState(() => read(PINNED_KEY) === '1');
  const [hover, setHover] = useState(false);
  const shrink = useRef<number | undefined>(undefined);
  /** الأبوابُ المطويّة — مفاتيحُها مفصولةٌ بفاصلة في مفتاحٍ واحد، فلا صفٌّ لكل باب. */
  const [closed, setClosed] = useState<Set<string>>(() => new Set(read(CLOSED_KEY).split(',').filter(Boolean)));

  useEffect(() => { write(PINNED_KEY, pinned ? '1' : '0'); }, [pinned]);
  // المؤقّت لا يبقى بعد التفكيك: الشاشة تُغلق والمهلة معلَّقة، فتُلغى
  useEffect(() => () => window.clearTimeout(shrink.current), []);

  /** الدخول يفتح فورًا، والخروج يُمهل ثانيتين — ولوحةُ المفاتيح كالماوس. */
  const enter = useCallback(() => { window.clearTimeout(shrink.current); setHover(true); }, []);
  const leave = useCallback(() => {
    window.clearTimeout(shrink.current);
    shrink.current = window.setTimeout(() => setHover(false), SHRINK_AFTER_MS);
  }, []);
  useEffect(() => { write(CLOSED_KEY, [...closed].join(',')); }, [closed]);

  const toggleSection = useCallback((key: string) => {
    setClosed(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const sections = groupTabs(tabs);
  const role = me.isOwner ? 'المالك' : ROLE_AR[me.role];
  const open = pinned || hover;
  const collapsed = !open;
  /**
   * **الاتّساع يدفع المحتوى ولا يعلو عليه** (قرار المالك 22/09/2026: «لما القائمة تفتح
   * ما تغطّيش على الكلام ده»): جُرّب أن تطفو فوق الصفحة كي لا ترتجّ، فكانت تحجب ما
   * تحتها — والحجبُ أسوأ من الإزاحة: المحجوب لا يُقرأ، والمُزاح يُقرأ في مكانه الجديد.
   * فالعمودُ يأخذ عرضه من الصفّ في الحالين، والصفحة تنساب معه في 200ms.
   */

  return (
    <div
      onMouseEnter={enter} onMouseLeave={leave}
      onFocusCapture={enter} onBlurCapture={leave}
      className={`hidden shrink-0 transition-[width] duration-200 ease-out lg:block
        ${open ? 'w-[19.5rem]' : 'w-[5.5rem]'}`}>
    <aside aria-label="عمود التنقّل"
      className={`sidebar-surface sticky top-0 flex h-screen flex-col border-e border-slate-200
        transition-[width] duration-200 ease-out
        ${open ? 'w-[19.5rem]' : 'w-[5.5rem]'}
        shadow-[1px_0_16px_-8px_rgba(15,23,42,.18)]`}>

      {/**
        * الاسم وحده — والشعارُ الكامل في الترويسة. وكانت معه علامةٌ مربّعة «iC» فأُزيلت
        * (قرار المالك 22/09/2026: «شيل iC دي خالص»): شعارٌ ثانٍ مختصَرٌ بجانب شعارٍ
        * كاملٍ في الشاشة نفسها تكرارٌ لا هوية.
        */}
      <div className="flex h-[4.25rem] items-center border-b border-[#a8dbe2] px-4">
        {collapsed ? (
          <span aria-hidden="true" className="mx-auto h-1 w-7 rounded-full bg-slate-200" />
        ) : (
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[17px] font-extrabold leading-tight text-brand-700">iConTravel</span>
            <span className="block truncate text-xs leading-tight text-ink-500">نظام الإدارة المتكامل</span>
          </span>
        )}
      </div>

      {/* الوجهات — العمود يطول فيُمرَّر وحده، والهوية والحساب ثابتان */}
      <nav aria-label="التنقل الرئيسي" className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-3 py-3">
        {sections.map(({ section, items }, i) => {
          /**
           * **بابٌ بلا عنوان لا يُطوى**: لا مقبضَ له يُعيده، فلو بقي مطويًّا في
           * الذاكرة من يومٍ كان له عنوان لاختفت شاشاتُه بلا سبيلٍ إلى ردّها (قرار
           * المالك 22/09/2026: «الحجوزات والمتابعات اليومية مش موجودة عندي»).
           */
          const folded = section.label !== '' && closed.has(section.key);
          const hasActive = items.some(t => t.id === active);
          return (
            <div key={section.key} className={i === 0 ? '' : 'mt-1.5'}>
              {/**
                * **عنوانُ الباب غيرُ بنوده** (قرار المالك 23/09/2026: «لازم الـdrop list تبقى لون
                * غير العنوان علشان الشكل كدا ملخبط جامد… أنا حاسس إني مشتّت وكل حاجة داخلة في
                * بعضها»): كان العنوان رماديًّا بخطّ البنود نفسه فاختلط بها. صار **شريطًا بلون
                * سماء الشعار** (`#8EE1E6`، لونُ أيقونة التطبيق نفسه) بنصٍّ كحليٍّ عريض ورمزٍ في
                * مربّعٍ أبيض — فثلاثُ درجاتٍ لا تختلط: **شريطٌ سماويّ** للباب، **قرصٌ كحليّ** للشاشة
                * المفتوحة، **سطرٌ رماديّ** للبند. وفسحةٌ أوسع قبل كل باب فلا يلتصق بما فوقه.
                */}
              {section.label !== '' && !collapsed && (
                <button type="button" onClick={() => toggleSection(section.key)} aria-expanded={!folded}
                  className="mt-3.5 mb-1.5 flex w-full items-center gap-2 rounded-lg border border-[#8EE1E6]
                    bg-[#8EE1E6]/55 px-2 py-1.5 text-start text-[13px] font-extrabold text-brand-900
                    shadow-sm shadow-[#8EE1E6]/40 transition-all duration-200 ease-out
                    hover:-translate-y-px hover:bg-[#8EE1E6]/80 active:scale-[0.99]">
                  <span aria-hidden="true"
                    className="grid h-7 w-7 shrink-0 place-items-center rounded-md bg-white text-brand-700 shadow-sm">
                    <span className="h-[1.05rem] w-[1.05rem]">{navIcon(section.icon)}</span>
                  </span>
                  <span className="flex-1">{section.label}</span>
                  {folded && hasActive && (
                    <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full" style={{ background: SEA }} />
                  )}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"
                    strokeLinejoin="round" aria-hidden="true"
                    className={`h-3.5 w-3.5 transition-transform duration-200 ${folded ? '' : 'rotate-180'}`}>
                    <path d="m6 9 6 6 6-6" />
                  </svg>
                </button>
              )}
              {/* في الوضع المطويّ لا عناوين تُقرأ — فخطٌّ يفصل الأبواب */}
              {section.label !== '' && collapsed && i > 0 && (
                <div aria-hidden="true" className="mx-2 my-2 border-t border-slate-200" />
              )}
              {(!folded || collapsed) && (
                <ul className={`space-y-1 ${section.label !== '' && !collapsed ? 'ms-2 border-s-2 border-[#8EE1E6] ps-1.5' : ''}`}>
                  {items.map(t => (
                    <li key={t.id}>
                      <Item tab={t} on={t.id === active} collapsed={collapsed} onClick={() => onPick(t.id)} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </nav>

      {/* صاحب الجلسة: اسمه ودوره وخروجه — ومعه مقبضُ طيّ العمود */}
      <div className="border-t border-slate-200/80 bg-white/70 p-3">
        <div className={`flex items-center gap-2.5 ${collapsed ? 'flex-col' : ''}`}>
          <span aria-hidden="true"
            className="sidebar-avatar grid h-10 w-10 shrink-0 place-items-center rounded-full text-[15px] font-extrabold
              text-brand-700 ring-1 ring-inset ring-brand-100">
            {me.fullNameAr.trim().charAt(0) || '؟'}
          </span>
          {!collapsed && (
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[14px] font-bold leading-tight text-ink-900">{me.fullNameAr}</span>
              <span className="block truncate text-[12px] leading-tight text-ink-500">{role}</span>
            </span>
          )}
        </div>
        {/**
          * **التثبيت**: من أراد القائمة مفتوحةً دائمًا ثبّتها، فلا تنكمش عند إبعاد
          * الماوس. وغيرُ المثبَّتة شريطُ رموزٍ يتّسع تحت الماوس وينكمش بعده بمهلة.
          * والزرّ لا يظهر إلّا والقائمة مفتوحة — ففي الشريط الضيّق لا موضع لنصّه.
          */}
        {open && (
          <button type="button" onClick={() => setPinned(v => !v)} aria-pressed={pinned}
            aria-label={pinned ? 'إلغاء تثبيت القائمة' : 'تثبيت القائمة مفتوحة'}
            title={pinned ? 'إلغاء تثبيت القائمة' : 'تثبيت القائمة مفتوحة'}
            className={`mt-2.5 flex w-full items-center justify-center gap-2 rounded-xl border py-2 text-xs
              font-bold transition-all duration-200 active:scale-[0.98]
              ${pinned
                ? 'border-brand-200 bg-brand-50 text-brand-700'
                : 'border-slate-200 bg-white text-ink-500 hover:border-brand-200 hover:bg-brand-50 hover:text-brand-700'}`}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"
              strokeLinejoin="round" aria-hidden="true" className="h-4 w-4">
              <path d="M9 4.5h6l-.8 5.2 3.1 3.1H6.7l3.1-3.1z" /><path d="M12 12.8V20" />
            </svg>
            {pinned ? 'إلغاء التثبيت' : 'تثبيت القائمة'}
          </button>
        )}
      </div>
    </aside>
    </div>
  );
}

/**
 * بندٌ في العمود — **طبقتان لا طبقة**: صفٌّ ورمزٌ في مربّعه.
 *
 * والحالة تُقرأ بلا لونٍ أيضًا: المفتوح أثقل وزنًا وله خطٌّ على حافة البداية، ومعه
 * `aria-current` لقارئ الشاشة. والانتقال 150ms — يُلمَس ولا يُنتظر.
 */
function Item({ tab, on, collapsed, onClick }: {
  tab: NavItem; on: boolean; collapsed: boolean; onClick: () => void;
}) {
  const badge = tab.badge !== undefined && tab.badge > 0 ? tab.badge : null;
  return (
    <button type="button" onClick={onClick} aria-current={on ? 'page' : undefined}
      title={collapsed ? tab.label : undefined}
      className={`group relative flex w-full items-center rounded-xl text-start text-sm outline-none
        transition-all duration-200 ease-out focus-visible:ring-2 focus-visible:ring-brand-500
        focus-visible:ring-offset-1 active:scale-[0.98]
        ${collapsed ? 'h-12 justify-center px-0' : 'h-12 gap-3 px-3'}
        ${on
          ? 'bg-gradient-to-l from-brand-600 to-brand-700 font-bold text-white shadow-md shadow-brand-600/25'
          : 'font-medium text-ink-700 ring-1 ring-transparent hover:-translate-y-px hover:bg-brand-50 hover:text-brand-800 hover:shadow-md hover:shadow-brand-600/20 hover:ring-brand-200'}`}>
      {on && (
        <span aria-hidden="true" className="absolute inset-y-2.5 -start-3 w-[3px] rounded-full"
          style={{ background: SEA }} />
      )}
      <span aria-hidden="true"
        className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg transition-all duration-200
          ${on
            ? 'bg-white/20 text-white'
            : 'bg-slate-100 text-ink-500 group-hover:scale-110 group-hover:bg-brand-600 group-hover:text-white group-hover:shadow-sm group-hover:shadow-brand-600/40'}`}>
        <span className="h-[1.3rem] w-[1.3rem]">{navIcon(tab.id)}</span>
      </span>
      {!collapsed && <span className="min-w-0 flex-1 truncate">{tab.label}</span>}
      {!collapsed && badge !== null && <Badge n={badge} on={on} />}
      {collapsed && badge !== null && (
        <span aria-hidden="true" className="absolute end-1.5 top-1.5 h-2.5 w-2.5 rounded-full bg-rose-500 ring-2 ring-white" />
      )}
      {/* مطويًّا: الاسم يظهر بجانب الرمز عند المرور — فلا وظيفةَ تختفي */}
      {collapsed && <Tip>{tab.label}</Tip>}
    </button>
  );
}

const Badge = ({ n, on }: { n: number; on: boolean }) => (
  <span className={`inline-flex min-w-[1.35rem] shrink-0 items-center justify-center rounded-full px-1.5 py-0.5
    text-xs font-bold leading-none tabular-nums shadow-sm
    ${on ? 'bg-white text-brand-700' : 'bg-rose-500 text-white'}`}>
    {n}
  </span>
);

/** تلميحةٌ إلى جهة المحتوى (يسار العمود في واجهةٍ عربية) — بلا حالةٍ ولا مؤقّت. */
const Tip = ({ children }: { children: ReactNode }) => (
  <span aria-hidden="true"
    className="pointer-events-none absolute end-full z-50 me-2 whitespace-nowrap rounded-lg bg-ink-900 px-2.5 py-1.5
      text-sm font-semibold text-white opacity-0 shadow-xl transition-opacity duration-150
      group-hover:opacity-100">
    {children}
  </span>
);
